import { useEffect, useRef } from "react";
import { Control, useWatch } from "react-hook-form";
import { useDebounce } from "use-debounce";

/**
 * Debounces form values and calls a callback when they change.
 * Use this to sync form state to an external store (e.g. accountApi) without
 * updating on every keystroke.
 *
 * @param control - react-hook-form Control
 * @param callback - called with parsed form data when values are stable (after delay ms)
 * @param options.delay - debounce delay in ms (default 500)
 */
export const useDebouncedHookFormValues = <T>(
  control: Control<T>,
  callback: (data: T) => void,
  options?: { delay?: number },
) => {
  const formValues = useWatch({ control });
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  // Always keep the latest form values in a ref so the unmount flush can access them.
  const latestFormValuesRef = useRef<T>(formValues as T);
  latestFormValuesRef.current = formValues as T;

  // Debounce a string so the input is stable when form content hasn't changed
  // (useWatch returns a new object ref each render, which would keep resetting the timer).
  const [debouncedJson] = useDebounce(
    JSON.stringify(formValues ?? {}),
    options?.delay ?? 250,
  );

  useEffect(() => {
    const data = JSON.parse(debouncedJson || "{}") as T;
    callbackRef.current?.(data);
  }, [debouncedJson]);

  // Flush the latest values synchronously on unmount so navigating away (back/forward)
  // never loses changes that were still pending in the debounce queue.
  useEffect(() => {
    return () => {
      callbackRef.current?.(latestFormValuesRef.current);
    };
  }, []);
};
